import { takeEvery, fork, put, all, call } from "redux-saga/effects"

// Redux Saga
import {
  GET_PRODUCTS_SUCCESS,
  GET_PRODUCTS_FAIL,
  GET_PRODUCT_SUCCESS,
  GET_PRODUCT_FAIL,
  CREATE_PRODUCT_SUCCESS,
  CREATE_PRODUCT_FAIL,
  UPDATE_PRODUCT_SUCCESS,
  UPDATE_PRODUCT_FAIL,
  DELETE_PRODUCT_SUCCESS,
  DELETE_PRODUCT_FAIL,
  ATTACH_PRODUCT_IMAGE_SUCCESS,
  ATTACH_PRODUCT_IMAGE_FAIL,
  UPLOAD_PRODUCT_IMAGE_SUCCESS,
  UPLOAD_PRODUCT_IMAGE_FAIL,
  UPDATE_PRODUCT_INVENTORY_SUCCESS,
  UPDATE_PRODUCT_INVENTORY_FAIL,
} from "./actionTypes"

// Products actions
import {
  getProducts as getProductsAction,
  getProduct as getProductAction,
  createProduct as createProductAction,
  updateProduct as updateProductAction,
  deleteProduct as deleteProductAction,
} from "./actions"

// API functions
import {
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  attachProductImage,
  uploadProductImage,
  updateProductInventory,
} from "../../helpers/backend_helper"

// Toast for notifications
import { toast } from "react-toastify"

// Get all products
function* fetchProducts({ payload: { params, history } }) {
  try {
    const response = yield call(getProducts, params)
    yield put({ type: GET_PRODUCTS_SUCCESS, payload: response })
  } catch (error) {
    yield put({ type: GET_PRODUCTS_FAIL, payload: error })
    toast.error("Failed to load products")
  }
}

// Get single product
function* fetchProduct({ payload: { productId, history } }) {
  try {
    const response = yield call(getProductById, productId)
    yield put({ type: GET_PRODUCT_SUCCESS, payload: response })
  } catch (error) {
    yield put({ type: GET_PRODUCT_FAIL, payload: error })
    toast.error("Failed to load product details")
  }
}

// Create product
function* onCreateProduct({ payload: { product, history } }) {
  try {
    const response = yield call(createProduct, product)
    console.log("Create product response:", response)

    // Check if response is valid and doesn't contain error
    if (!response || (response.message && response.message.includes("Error")) || response.error || (response.title && response.title.includes("Error"))) {
      const errorMsg = response?.message || response?.error || response?.title || "Failed to create product"
      toast.error(errorMsg)
      yield put({ type: CREATE_PRODUCT_FAIL, payload: errorMsg })
      return
    }

    // Success - no error in response
    yield put({ type: CREATE_PRODUCT_SUCCESS, payload: response })
    toast.success("Product created successfully!")
    // Refresh the products list
    yield put(getProductsAction({ PageSize: 10, PageIndex: 0 }, history))
  } catch (error) {
    yield put({ type: CREATE_PRODUCT_FAIL, payload: error })
    // Extract error message from response if available
    let errorMsg = "Failed to create product"

    if (error?.response) {
      // Handle axios error with response
      const data = error.response.data
      errorMsg = data?.message || data?.title || data?.error || `Server Error (${error.response.status})`
    } else if (error?.message) {
      // Handle network error or other errors
      errorMsg = error.message
    } else if (typeof error === "string") {
      errorMsg = error
    }

    toast.error(errorMsg)
  }
}

// Update product
function* onUpdateProduct({ payload: { productId, product, history } }) {
  try {
    const response = yield call(updateProduct, productId, product)

    // Check if response is valid and doesn't contain error
    if (!response || (response.message && response.message.includes("Error")) || response.error || (response.title && response.title.includes("Error"))) {
      const errorMsg = response?.message || response?.error || response?.title || "Failed to update product"
      toast.error(errorMsg)
      yield put({ type: UPDATE_PRODUCT_FAIL, payload: errorMsg })
      return
    }

    // Success - no error in response
    yield put({ type: UPDATE_PRODUCT_SUCCESS, payload: response })
    toast.success("Product updated successfully!")
    // Refresh the products list
    yield put(getProductsAction({ PageSize: 10, PageIndex: 0 }, history))
  } catch (error) {
    yield put({ type: UPDATE_PRODUCT_FAIL, payload: error })
    // Extract error message from response if available
    let errorMsg = "Failed to update product"

    if (error?.response) {
      // Handle axios error with response
      const data = error.response.data
      errorMsg = data?.message || data?.title || data?.error || `Server Error (${error.response.status})`
    } else if (error?.message) {
      // Handle network error or other errors
      errorMsg = error.message
    } else if (typeof error === "string") {
      errorMsg = error
    }

    toast.error(errorMsg)
  }
}

// Delete product
function* onDeleteProduct({ payload: { productId, history } }) {
  try {
    const response = yield call(deleteProduct, productId)
    yield put({ type: DELETE_PRODUCT_SUCCESS, payload: response })
    toast.success("Product deleted successfully!")
    // Refresh the products list
    yield put(getProductsAction({ PageSize: 10, PageIndex: 0 }, history))
  } catch (error) {
    yield put({ type: DELETE_PRODUCT_FAIL, payload: error })
    toast.error("Failed to delete product")
  }
}

// Attach existing document as product image
function* onAttachProductImage({ payload: { productId, documentId, isPrimary, history } }) {
  try {
    const response = yield call(attachProductImage, productId, documentId, isPrimary)
    yield put({ type: ATTACH_PRODUCT_IMAGE_SUCCESS, payload: response })
    toast.success("Product image attached successfully!")
    // Refresh product details
    yield put(getProductAction(productId, history))
  } catch (error) {
    yield put({ type: ATTACH_PRODUCT_IMAGE_FAIL, payload: error })
    toast.error("Failed to attach product image")
  }
}

// Upload base64 image and link to product
function* onUploadProductImage({ payload: { productId, imageData, history } }) {
  try {
    console.log("Uploading image for product ID:", productId)
    const response = yield call(uploadProductImage, productId, imageData)
    yield put({ type: UPLOAD_PRODUCT_IMAGE_SUCCESS, payload: response })
    toast.success("Product image uploaded successfully!")
    // Refresh product details
    yield put(getProductAction(productId, history))
  } catch (error) {
    yield put({ type: UPLOAD_PRODUCT_IMAGE_FAIL, payload: error })
    // Extract error message from response if available
    let errorMsg = "Failed to upload product image"
    if (error?.response) {
      const data = error.response.data
      errorMsg = data?.message || data?.title || data?.error || `Server Error (${error.response.status})`
    } else if (error?.message) {
      errorMsg = error.message
    }
    toast.error(errorMsg)
  }
}

// Update product inventory by terminal
function* onUpdateProductInventory({ payload: { productId, items, history } }) {
  try {
    const response = yield call(updateProductInventory, productId, items)
    yield put({ type: UPDATE_PRODUCT_INVENTORY_SUCCESS, payload: response })
    toast.success("Product inventory updated successfully!")
    // Refresh product details
    yield put(getProductAction(productId, history))
  } catch (error) {
    yield put({ type: UPDATE_PRODUCT_INVENTORY_FAIL, payload: error })
    toast.error("Failed to update product inventory")
  }
}

// Watchers
function* getProductsSaga() {
  yield takeEvery("GET_PRODUCTS", fetchProducts)
}

function* getProductSaga() {
  yield takeEvery("GET_PRODUCT", fetchProduct)
}

function* createProductSaga() {
  yield takeEvery("CREATE_PRODUCT", onCreateProduct)
}

function* updateProductSaga() {
  yield takeEvery("UPDATE_PRODUCT", onUpdateProduct)
}

function* deleteProductSaga() {
  yield takeEvery("DELETE_PRODUCT", onDeleteProduct)
}

function* attachProductImageSaga() {
  yield takeEvery("ATTACH_PRODUCT_IMAGE", onAttachProductImage)
}

function* uploadProductImageSaga() {
  yield takeEvery("UPLOAD_PRODUCT_IMAGE", onUploadProductImage)
}

function* updateProductInventorySaga() {
  yield takeEvery("UPDATE_PRODUCT_INVENTORY", onUpdateProductInventory)
}

// Export all sagas
function* productsSaga() {
  yield all([
    fork(getProductsSaga),
    fork(getProductSaga),
    fork(createProductSaga),
    fork(updateProductSaga),
    fork(deleteProductSaga),
    fork(attachProductImageSaga),
    fork(uploadProductImageSaga),
    fork(updateProductInventorySaga),
  ])
}

export default productsSaga